import { toPlainText } from '../../lib/contentSafety';

export const formatProgramDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const validNumber = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const ageLabel = (min, max) => {
  if (validNumber(min) && validNumber(max)) return min <= max ? `${min}–${max} years` : 'Not provided';
  if (validNumber(min)) return `From ${min} years · maximum not provided`;
  if (validNumber(max)) return `Up to ${max} years · minimum not provided`;
  return 'Not provided';
};

const hasUnsafeURLCharacters = (value) => /["<>\\]/.test(value) || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);

export const safeWebsite = (value) => {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value) || /\s/.test(value) || hasUnsafeURLCharacters(value)) return '';
  try {
    const url = new URL(value);
    return url.hostname && ['http:', 'https:'].includes(url.protocol) ? value : '';
  } catch {
    return '';
  }
};
export const safePublicImage = (value) => {
  if (typeof value !== 'string' || hasUnsafeURLCharacters(value)) return '';
  return safeWebsite(value) || (/^\/(?!\/)/.test(value) || /^data:image\/(png|jpeg|webp|gif);base64,[a-z0-9+/=]+$/i.test(value) ? value : '');
};
// WCAG relative luminance, not perceived-brightness heuristics.
const relativeLuminance = hex => {
  const channels = hex.slice(1).match(/../g).map(channel => {
    const value = parseInt(channel, 16) / 255;
    return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
  });
  return channels[0] * .2126 + channels[1] * .7152 + channels[2] * .0722;
};
export const publicActionStyle = (org = {}) => {
  const primary = /^#[0-9a-f]{6}$/i.test(org.primary_color || '') ? org.primary_color : '#101a19';
  const luminance = relativeLuminance(primary);
  const whiteContrast = 1.05 / (luminance + .05);
  const darkContrast = (luminance + .05) / (relativeLuminance('#050505') + .05);
  return {
    '--public-action-bg': primary,
    '--public-action-ink': whiteContrast >= darkContrast ? '#ffffff' : '#050505',
  };
};

// Detail-only tokens; catalog branding and action defaults remain unchanged.
export const publicDetailStyle = (org = {}) => {
  const action = publicActionStyle(org);
  const secondary = publicActionStyle({primary_color: org.secondary_color || action['--public-action-bg']});
  const primary = action['--public-action-bg'];
  return {
    ...action,
    '--public-secondary-bg': secondary['--public-action-bg'],
    '--public-secondary-ink': secondary['--public-action-ink'],
    '--public-link': 1.05 / (relativeLuminance(primary) + .05) >= 4.5 ? primary : '#101a19',
  };
};

export const publicBrandStyle = (org = {}) => {
  const color = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;
  const primary = color(org.primary_color, '#101a19');
  const secondary = color(org.secondary_color, primary);
  const photo = safePublicImage(org.sidebar_image_url);
  return { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), ${photo ? `url("${photo}")` : `linear-gradient(155deg, ${primary}, ${secondary})`}` };
};

export function presentProgram(program = {}) {
  const divisions = Array.isArray(program.divisions) ? program.divisions.filter(d => d && typeof d === 'object') : [];
  const prices = divisions.length ? divisions.map(d => d.price) : [program.price];
  const knownPrices = prices.every(validNumber);
  const min = Math.min(...prices), max = Math.max(...prices);
  const start = formatProgramDate(program.start_date), end = formatProgramDate(program.end_date);
  const programAges = ageLabel(program.age_min, program.age_max);
  const showProgramAges = programAges !== 'Not provided' && divisions.some(d => ageLabel(d.age_min, d.age_max) === 'Not provided');
  const divisionAges = divisions.map(d => {
    const ages = ageLabel(d.age_min, d.age_max);
    return `${toPlainText(d.name) || 'Division'}${showProgramAges && ages === 'Not provided' ? ' eligibility' : ''}: ${ages}`;
  });
  return {
    name: toPlainText(program.name) || 'Unnamed program',
    description: toPlainText(program.description) || 'Description not provided.',
    ages: divisions.length
      ? [...(showProgramAges ? [`Program ages: ${programAges}`] : []), ...divisionAges].join(' · ')
      : programAges,
    genders: divisions.length ? divisions.map(d => `${toPlainText(d.name) || 'Division'}: ${toPlainText(d.gender) ? toPlainText(d.gender).charAt(0).toUpperCase() + toPlainText(d.gender).slice(1) : 'Not provided'}`).join(' · ') : 'Not provided',
    dates: start && end ? `${start} – ${end}` : start ? `${start} – End date not provided` : end ? `Start date not provided – ${end}` : 'Dates not provided',
    schedule: toPlainText(program.schedule) || 'Times not provided',
    location: [toPlainText(program.location), toPlainText(program.venue_address)].filter(Boolean).join(' · ') || 'Location not provided',
    price: knownPrices ? min === max ? `$${min}` : `$${min} – $${max}` : 'Price not provided',
  };
}
